import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Tag,
  Plus,
  Search,
  X,
  Edit,
  Trash2,
  Copy,
  Check,
  Percent,
  Coins,
  Calendar,
  Users,
  ShoppingBag,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Sparkles,
  ArrowUpDown,
  RefreshCw,
} from "lucide-react";
import { useToast } from "../../context/ToastContext";
import * as adminApi from "../../api/adminApi";
import { formatMoney } from "../../utils/format";
import "./AdminCoupons.css";

const COUPON_TYPES = {
  PERCENTAGE: 0,
  FIXED: 1,
};

const isPercentageCoupon = (type) =>
  type === 0 ||
  type === "Percentage" ||
  type === COUPON_TYPES.PERCENTAGE;

const isFixedCoupon = (type) =>
  type === 1 ||
  type === "Fixed" ||
  type === COUPON_TYPES.FIXED;

const initialForm = {
  code: "",
  couponType: COUPON_TYPES.PERCENTAGE,
  amount: "",
  minimumSpend: "",
  maximumSpend: "",
  maxNumberOfUsage: "",
  maxUsagePerUser: "1",
  startDate: new Date().toISOString().slice(0, 16),
  endDate: "",
  isEnabled: true,
};

export default function AdminCoupons() {
  const { addToast } = useToast();

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'active' | 'inactive' | 'expired'
  const [typeFilter, setTypeFilter] = useState("all"); // 'all' | 'Percentage' | 'Fixed'

  // Modal states
  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | null
  const [activeCoupon, setActiveCoupon] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Delete modal state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Toggling status state
  const [togglingId, setTogglingId] = useState(null);

  // Clipboard copy state
  const [copiedCode, setCopiedCode] = useState(null);

  const loadCoupons = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await adminApi.fetchAdminCoupons({ page: 1, pageSize: 50 });
      setCoupons(data?.items || []);
    } catch (e) {
      addToast(e.message || "Failed to load coupons.", "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  // Statistics
  const stats = useMemo(() => {
    const total = coupons.length;
    const active = coupons.filter(
      (c) => c.isEnabled && !c.isExpired
    ).length;
    const totalRedemptions = coupons.reduce(
      (sum, c) => sum + (Number(c.timesUsed) || 0),
      0
    );
    const expiredOrInactive = coupons.filter(
      (c) => !c.isEnabled || c.isExpired
    ).length;

    return { total, active, totalRedemptions, expiredOrInactive };
  }, [coupons]);

  // Filtered coupons
  const filteredCoupons = useMemo(() => {
    return coupons.filter((c) => {
      const q = searchQuery.trim().toUpperCase();
      const matchesSearch = !q || (c.code && c.code.toUpperCase().includes(q));

      const isPercentage = isPercentageCoupon(c.couponType);
      const isFixed = isFixedCoupon(c.couponType);

      const matchesType =
        typeFilter === "all" ||
        (typeFilter === COUPON_TYPES.PERCENTAGE && isPercentage) ||
        (typeFilter === COUPON_TYPES.FIXED && isFixed);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && c.isEnabled && !c.isExpired) ||
        (statusFilter === "inactive" && !c.isEnabled && !c.isExpired) ||
        (statusFilter === "expired" && c.isExpired);

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [coupons, searchQuery, statusFilter, typeFilter]);

  // Copy code to clipboard
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    addToast(`Coupon code "${code}" copied to clipboard!`, "info");
    setTimeout(() => {
      setCopiedCode((curr) => (curr === code ? null : curr));
    }, 2000);
  };

  // Toggle isEnabled status
  const handleToggleStatus = async (coupon) => {
    setTogglingId(coupon.id);
    const nextState = !coupon.isEnabled;
    try {
      await adminApi.updateAdminCoupon(coupon.id, { isEnabled: nextState });
      setCoupons((prev) =>
        prev.map((c) => (c.id === coupon.id ? { ...c, isEnabled: nextState } : c))
      );
      addToast(
        `Coupon "${coupon.code}" is now ${nextState ? "Active" : "Inactive"}.`,
        "success"
      );
    } catch (e) {
      addToast(e.message || "Failed to update coupon status.", "error");
    } finally {
      setTogglingId(null);
    }
  };

  // Open Create Modal
  const openCreateModal = () => {
    setFormData({
      ...initialForm,
      startDate: new Date().toISOString().slice(0, 16),
    });
    setFormErrors({});
    setModalMode("create");
  };

  // Open Edit Modal
  const openEditModal = (coupon) => {
    setActiveCoupon(coupon);
    const isPercentage = isPercentageCoupon(coupon.couponType);

    setFormData({
      code: coupon.code || "",
      couponType: isPercentage
        ? COUPON_TYPES.PERCENTAGE
        : COUPON_TYPES.FIXED,
      amount: coupon.amount != null ? String(coupon.amount) : "",
      minimumSpend:
        coupon.minimumSpend != null && coupon.minimumSpend > 0
          ? String(coupon.minimumSpend)
          : "",
      maximumSpend:
        coupon.maximumSpend != null ? String(coupon.maximumSpend) : "",
      maxNumberOfUsage:
        coupon.maxNumberOfUsage != null ? String(coupon.maxNumberOfUsage) : "",
      maxUsagePerUser:
        coupon.maxUsagePerUser != null ? String(coupon.maxUsagePerUser) : "1",
      startDate: coupon.startDate
        ? new Date(coupon.startDate).toISOString().slice(0, 16)
        : new Date().toISOString().slice(0, 16),
      endDate: coupon.endDate
        ? new Date(coupon.endDate).toISOString().slice(0, 16)
        : "",
      isEnabled: coupon.isEnabled ?? true,
    });
    setFormErrors({});
    setModalMode("edit");
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!formData.code.trim()) {
      errors.code = "Coupon code is required.";
    } else if (!/^[A-Za-z0-9_\-]+$/.test(formData.code.trim())) {
      errors.code = "Code can only contain letters, numbers, hyphens, and underscores.";
    }

    const amt = parseFloat(formData.amount);
    const isPercent = isPercentageCoupon(formData.couponType);

    if (isNaN(amt) || amt <= 0) {
      errors.amount = "Amount must be greater than 0.";
    } else if (isPercent && amt > 100) {
      errors.amount = "Percentage discount cannot exceed 100%.";
    }

    const minSpend = parseFloat(formData.minimumSpend);
    if (formData.minimumSpend && (isNaN(minSpend) || minSpend < 0)) {
      errors.minimumSpend = "Minimum spend cannot be negative.";
    }

    const maxSpend = parseFloat(formData.maximumSpend);
    if (formData.maximumSpend) {
      if (isNaN(maxSpend) || maxSpend <= 0) {
        errors.maximumSpend = "Maximum spend must be greater than 0.";
      } else if (!isNaN(minSpend) && maxSpend < minSpend) {
        errors.maximumSpend = "Maximum spend cannot be less than minimum spend.";
      }
    }

    const maxUsage = parseInt(formData.maxNumberOfUsage, 10);
    if (formData.maxNumberOfUsage && (isNaN(maxUsage) || maxUsage <= 0)) {
      errors.maxNumberOfUsage = "Max redemptions must be greater than 0.";
    }

    const perUser = parseInt(formData.maxUsagePerUser, 10);
    if (formData.maxUsagePerUser && (isNaN(perUser) || perUser <= 0)) {
      errors.maxUsagePerUser = "Max per user must be greater than 0.";
    }

    if (formData.startDate && formData.endDate) {
      if (new Date(formData.endDate) <= new Date(formData.startDate)) {
        errors.endDate = "End date must be later than start date.";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit Create or Edit
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      const isPercent = isPercentageCoupon(formData.couponType);

      const payload = {
        code: formData.code.trim().toUpperCase(),
        couponType: isPercent ? 0 : 1,
        amount: Number(formData.amount),
        minimumSpend: formData.minimumSpend ? Number(formData.minimumSpend) : 0,
        maximumSpend: formData.maximumSpend ? Number(formData.maximumSpend) : null,
        maxNumberOfUsage: formData.maxNumberOfUsage
          ? Number(formData.maxNumberOfUsage)
          : null,
        maxUsagePerUser: formData.maxUsagePerUser
          ? Number(formData.maxUsagePerUser)
          : null,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
        isEnabled: formData.isEnabled,
      };

      if (modalMode === "create") {
        const created = await adminApi.createAdminCoupon(payload);
        setCoupons((prev) => [created, ...prev]);
        addToast(`Coupon "${created.code}" created successfully!`, "success");
      } else {
        const updated = await adminApi.updateAdminCoupon(activeCoupon.id, payload);
        setCoupons((prev) =>
          prev.map((c) => (c.id === activeCoupon.id ? updated : c))
        );
        addToast(`Coupon "${updated.code}" updated successfully!`, "success");
      }

      setModalMode(null);
    } catch (e) {
      addToast(e.message || "Failed to save coupon.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Confirm Delete
  const handleDeleteCoupon = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminApi.deleteAdminCoupon(deleteTarget.id);
      setCoupons((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      addToast(`Coupon "${deleteTarget.code}" deleted.`, "success");
      setDeleteTarget(null);
    } catch (e) {
      addToast(e.message || "Failed to delete coupon.", "error");
    } finally {
      setDeleting(false);
    }
  };

  // Helper date formatter
  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="admin-coupons">
      {/* 1. Header */}
      <div className="admin-coupons__head">
        <div>
          <div className="admin-coupons__title-wrap">
            <h1 className="admin-coupons__title">Coupons & Discounts</h1>
            <span className="admin-coupons__count-badge">{coupons.length} total</span>
          </div>
          <p className="admin-coupons__desc">
            Manage promotional discount codes, usage limits, spending thresholds, and expiry windows.
          </p>
        </div>

        <div className="admin-coupons__head-actions">
          <button
            type="button"
            className="btn-outline admin-coupons__refresh-btn"
            onClick={() => loadCoupons(true)}
            disabled={loading || refreshing}
            title="Refresh coupons"
          >
            <RefreshCw
              size={15}
              className={refreshing ? "spin-animation" : ""}
            />
            <span>Refresh</span>
          </button>
          <button
            type="button"
            className="btn-primary admin-coupons__add-btn"
            onClick={openCreateModal}
          >
            <Plus size={16} />
            <span>Create Coupon</span>
          </button>
        </div>
      </div>

      {/* 2. Key Metrics Bar */}
      <div className="admin-coupons__metrics">
        <div className="admin-coupons__metric-card">
          <div className="admin-coupons__metric-icon total">
            <Tag size={18} />
          </div>
          <div>
            <div className="admin-coupons__metric-val">{stats.total}</div>
            <div className="admin-coupons__metric-label">Total Coupons</div>
          </div>
        </div>

        <div className="admin-coupons__metric-card">
          <div className="admin-coupons__metric-icon active">
            <CheckCircle2 size={18} />
          </div>
          <div>
            <div className="admin-coupons__metric-val">{stats.active}</div>
            <div className="admin-coupons__metric-label">Active Promotions</div>
          </div>
        </div>

        <div className="admin-coupons__metric-card">
          <div className="admin-coupons__metric-icon redemptions">
            <ShoppingBag size={18} />
          </div>
          <div>
            <div className="admin-coupons__metric-val">{stats.totalRedemptions}</div>
            <div className="admin-coupons__metric-label">Total Redemptions</div>
          </div>
        </div>

        <div className="admin-coupons__metric-card">
          <div className="admin-coupons__metric-icon expired">
            <Clock size={18} />
          </div>
          <div>
            <div className="admin-coupons__metric-val">{stats.expiredOrInactive}</div>
            <div className="admin-coupons__metric-label">Inactive / Expired</div>
          </div>
        </div>
      </div>

      {/* 3. Toolbar (Search & Filter Tabs) */}
      <div className="admin-coupons__toolbar">
        <div className="admin-coupons__search-box">
          <Search size={15} className="admin-coupons__search-icon" />
          <input
            type="text"
            className="admin-coupons__search-input"
            placeholder="Search coupon code (e.g. SUMMER20)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="admin-coupons__clear-search"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="admin-coupons__filters">
          <div className="admin-coupons__filter-group">
            <span className="admin-coupons__filter-label">Status:</span>
            <div className="admin-coupons__filter-tabs">
              {["all", "active", "inactive", "expired"].map((st) => (
                <button
                  key={st}
                  type="button"
                  className={`admin-coupons__tab ${statusFilter === st ? "active" : ""}`}
                  onClick={() => setStatusFilter(st)}
                >
                  {st.charAt(0).toUpperCase() + st.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="admin-coupons__filter-group">
            <span className="admin-coupons__filter-label">Type:</span>
            <div className="admin-coupons__filter-tabs">
              {[
                { id: "all", label: "All" },
                { id: COUPON_TYPES.PERCENTAGE, label: "% Off" },
                { id: COUPON_TYPES.FIXED, label: "Fixed Off" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`admin-coupons__tab ${typeFilter === t.id ? "active" : ""}`}
                  onClick={() => setTypeFilter(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 4. Coupons Table / List */}
      {loading ? (
        <div className="admin-coupons__loading">
          <Loader2 size={32} className="spin-animation" />
          <p>Loading coupons...</p>
        </div>
      ) : filteredCoupons.length === 0 ? (
        <div className="admin-coupons__empty">
          <Tag size={42} className="admin-coupons__empty-icon" />
          <h3>No coupons found</h3>
          <p>
            {searchQuery || statusFilter !== "all" || typeFilter !== "all"
              ? "No coupons match your filter criteria. Try adjusting your search."
              : "Get started by creating your first promotional coupon."}
          </p>
          {(searchQuery || statusFilter !== "all" || typeFilter !== "all") && (
            <button
              type="button"
              className="btn-outline admin-coupons__reset-btn"
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("all");
                setTypeFilter("all");
              }}
            >
              Reset Filters
            </button>
          )}
        </div>
      ) : (
        <div className="admin-coupons__table-wrap">
          <table className="admin-coupons__table">
            <thead>
              <tr>
                <th>Coupon Code</th>
                <th>Discount</th>
                <th>Spend Rules</th>
                <th>Usage & Limits</th>
                <th>Validity Window</th>
                <th>Status</th>
                <th className="actions-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCoupons.map((coupon) => {
                const isPercentage = isPercentageCoupon(coupon.couponType);

                const usageRatio =
                  coupon.maxNumberOfUsage && coupon.maxNumberOfUsage > 0
                    ? Math.min(
                        100,
                        Math.round((coupon.timesUsed / coupon.maxNumberOfUsage) * 100)
                      )
                    : null;

                const isCurrentlyActive = coupon.isEnabled && !coupon.isExpired;

                return (
                  <tr
                    key={coupon.id}
                    className={`admin-coupons__row ${!coupon.isEnabled ? "disabled-row" : ""}`}
                  >
                    {/* Column 1: Code */}
                    <td className="admin-coupons__code-cell">
                      <div className="admin-coupons__code-box">
                        <span className="admin-coupons__code-text">
                          {coupon.code}
                        </span>
                        <button
                          type="button"
                          className="admin-coupons__copy-btn"
                          title="Copy coupon code"
                          onClick={() => handleCopyCode(coupon.code)}
                        >
                          {copiedCode === coupon.code ? (
                            <Check size={13} className="text-success" />
                          ) : (
                            <Copy size={13} />
                          )}
                        </button>
                      </div>
                    </td>

                    {/* Column 2: Discount */}
                    <td className="admin-coupons__discount-cell">
                      <div className="admin-coupons__discount-badge-wrap">
                        <span
                          className={`admin-coupons__discount-badge ${
                            isPercentage ? "percentage" : "fixed"
                          }`}
                        >
                          {isPercentage ? (
                            <>
                              <Percent size={12} />
                              <span>{coupon.amount}% OFF</span>
                            </>
                          ) : (
                            <>
                              <Coins size={12} />
                              <span>{formatMoney(coupon.amount)} OFF</span>
                            </>
                          )}
                        </span>
                      </div>
                    </td>

                    {/* Column 3: Spend Rules */}
                    <td className="admin-coupons__spend-cell">
                      <div className="admin-coupons__spend-info">
                        <div className="admin-coupons__spend-item">
                          <span className="spend-key">Min Spend:</span>
                          <span className="spend-val">
                            {coupon.minimumSpend > 0
                              ? formatMoney(coupon.minimumSpend)
                              : "None"}
                          </span>
                        </div>
                        {isPercentage && coupon.maximumSpend && (
                          <div className="admin-coupons__spend-item">
                            <span className="spend-key">Spend Cap:</span>
                            <span className="spend-val">
                              {formatMoney(coupon.maximumSpend)}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Column 4: Usage & Limits */}
                    <td className="admin-coupons__usage-cell">
                      <div className="admin-coupons__usage-box">
                        <div className="admin-coupons__usage-numbers">
                          <strong>{coupon.timesUsed}</strong>
                          <span className="text-muted">
                            / {coupon.maxNumberOfUsage ? coupon.maxNumberOfUsage : "∞"} uses
                          </span>
                        </div>
                        {usageRatio !== null && (
                          <div className="admin-coupons__progress-bar">
                            <div
                              className={`admin-coupons__progress-fill ${
                                usageRatio >= 100 ? "full" : ""
                              }`}
                              style={{ width: `${usageRatio}%` }}
                            />
                          </div>
                        )}
                        <span className="admin-coupons__per-user">
                          {coupon.maxUsagePerUser
                            ? `Max ${coupon.maxUsagePerUser} / user`
                            : "Unlimited / user"}
                        </span>
                      </div>
                    </td>

                    {/* Column 5: Validity Window */}
                    <td className="admin-coupons__date-cell">
                      <div className="admin-coupons__date-info">
                        <div className="admin-coupons__date-range">
                          <span>{formatDate(coupon.startDate)}</span>
                          <span className="arrow-sep">→</span>
                          <span
                            className={coupon.isExpired ? "text-danger" : ""}
                          >
                            {formatDate(coupon.endDate)}
                          </span>
                        </div>
                        {coupon.isExpired ? (
                          <span className="admin-coupons__status-pill expired">
                            Expired
                          </span>
                        ) : (
                          <span className="admin-coupons__status-pill valid">
                            {coupon.endDate ? "Active Period" : "No Expiration"}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Column 6: Status Toggle */}
                    <td className="admin-coupons__status-cell">
                      <label
                        className={`admin-coupons__switch ${
                          togglingId === coupon.id ? "loading" : ""
                        }`}
                        title={coupon.isEnabled ? "Click to disable" : "Click to activate"}
                      >
                        <input
                          type="checkbox"
                          checked={coupon.isEnabled}
                          disabled={togglingId === coupon.id}
                          onChange={() => handleToggleStatus(coupon)}
                        />
                        <span className="admin-coupons__slider" />
                      </label>
                      <span
                        className={`admin-coupons__switch-label ${
                          coupon.isEnabled ? "active-txt" : "inactive-txt"
                        }`}
                      >
                        {coupon.isEnabled ? "Active" : "Disabled"}
                      </span>
                    </td>

                    {/* Column 7: Actions */}
                    <td className="admin-coupons__actions-cell">
                      <div className="admin-coupons__row-actions">
                        <button
                          type="button"
                          className="admin-coupons__action-icon-btn edit"
                          title="Edit coupon details"
                          onClick={() => openEditModal(coupon)}
                        >
                          <Edit size={15} />
                        </button>
                        <button
                          type="button"
                          className="admin-coupons__action-icon-btn delete"
                          title="Delete coupon"
                          onClick={() => setDeleteTarget(coupon)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 5. CREATE / EDIT MODAL */}
      {modalMode && (
        <div
          className="admin-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setModalMode(null)}
        >
          <div
            className="admin-modal admin-coupons__modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal__header">
              <div className="admin-coupons__modal-title-wrap">
                <Tag size={20} className="text-gold" />
                <h3>
                  {modalMode === "create" ? "Create New Coupon" : "Edit Coupon"}
                </h3>
              </div>
              <button
                type="button"
                className="admin-modal__close"
                onClick={() => setModalMode(null)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmitForm} className="admin-coupons__form">
              {/* Code & Type */}
              <div className="admin-coupons__form-row">
                <div className="admin-coupons__field flex-1">
                  <label htmlFor="coupon-code">
                    Coupon Code <span className="req">*</span>
                  </label>
                  <input
                    id="coupon-code"
                    type="text"
                    className={`admin-coupons__input uppercase ${
                      formErrors.code ? "has-error" : ""
                    }`}
                    placeholder="e.g. FLASH30, WELCOME10"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        code: e.target.value.toUpperCase(),
                      })
                    }
                    autoFocus
                  />
                  {formErrors.code && (
                    <span className="admin-coupons__error-text">
                      {formErrors.code}
                    </span>
                  )}
                </div>

                <div className="admin-coupons__field flex-1">
                  <label>Discount Type</label>
                  <div className="admin-coupons__type-toggle">
                    <button
                      type="button"
                      className={`type-btn ${
                        isPercentageCoupon(formData.couponType)
                          ? "selected"
                          : ""
                      }`}
                      onClick={() =>
                        setFormData({
                          ...formData,
                          couponType: COUPON_TYPES.PERCENTAGE,
                        })
                      }
                    >
                      <Percent size={14} />
                      <span>Percentage (%)</span>
                    </button>
                    <button
                      type="button"
                      className={`type-btn ${
                        isFixedCoupon(formData.couponType)
                          ? "selected"
                          : ""
                      }`}
                      onClick={() =>
                        setFormData({
                          ...formData,
                          couponType: COUPON_TYPES.FIXED,
                        })
                      }
                    >
                      <Coins size={14} />
                      <span>Fixed Amount</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Amount */}
              <div className="admin-coupons__form-row">
                <div className="admin-coupons__field flex-1">
                  <label htmlFor="coupon-amount">
                    {isPercentageCoupon(formData.couponType)
                      ? "Percentage Discount (%)"
                      : "Discount Amount (EGP)"}{" "}
                    <span className="req">*</span>
                  </label>
                  <input
                    id="coupon-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    max={
                      isPercentageCoupon(formData.couponType)
                        ? "100"
                        : undefined
                    }
                    className={`admin-coupons__input ${
                      formErrors.amount ? "has-error" : ""
                    }`}
                    placeholder={
                      isPercentageCoupon(formData.couponType)
                        ? "e.g. 20 (for 20% off)"
                        : "e.g. 50.00"
                    }
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                  />
                  {formErrors.amount && (
                    <span className="admin-coupons__error-text">
                      {formErrors.amount}
                    </span>
                  )}
                </div>

                <div className="admin-coupons__field flex-1">
                  <label htmlFor="coupon-min-spend">
                    Minimum Cart Spend (EGP)
                  </label>
                  <input
                    id="coupon-min-spend"
                    type="number"
                    step="0.01"
                    min="0"
                    className="admin-coupons__input"
                    placeholder="e.g. 150 (Leave 0 for no minimum)"
                    value={formData.minimumSpend}
                    onChange={(e) =>
                      setFormData({ ...formData, minimumSpend: e.target.value })
                    }
                  />
                  <span className="admin-coupons__hint">
                    Cart subtotal required before coupon applies.
                  </span>
                </div>
              </div>

              {/* Maximum Spend Cap (Percentage Only) & Max Redemptions */}
              <div className="admin-coupons__form-row">
                {isPercentageCoupon(formData.couponType) ? (
                  <div className="admin-coupons__field flex-1">
                    <label htmlFor="coupon-max-spend">
                      Max Eligible Spend Cap (EGP)
                    </label>
                    <input
                      id="coupon-max-spend"
                      type="number"
                      step="0.01"
                      min="0"
                      className={`admin-coupons__input ${
                        formErrors.maximumSpend ? "has-error" : ""
                      }`}
                      placeholder="e.g. 1000 (Optional)"
                      value={formData.maximumSpend}
                      onChange={(e) =>
                        setFormData({ ...formData, maximumSpend: e.target.value })
                      }
                    />
                    <span className="admin-coupons__hint">
                      Maximum cart amount the % discount can be calculated on.
                    </span>
                    {formErrors.maximumSpend && (
                      <span className="admin-coupons__error-text">
                        {formErrors.maximumSpend}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="admin-coupons__field flex-1">
                    <label htmlFor="coupon-user-limit">
                      Max Uses Per Customer
                    </label>
                    <input
                      id="coupon-user-limit"
                      type="number"
                      min="1"
                      className="admin-coupons__input"
                      placeholder="e.g. 1 (Default: 1 per customer)"
                      value={formData.maxUsagePerUser}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          maxUsagePerUser: e.target.value,
                        })
                      }
                    />
                    <span className="admin-coupons__hint">
                      Limits redemptions for each customer.
                    </span>
                  </div>
                )}

                <div className="admin-coupons__field flex-1">
                  <label htmlFor="coupon-max-usage">
                    Total Storewide Redemptions
                  </label>
                  <input
                    id="coupon-max-usage"
                    type="number"
                    min="1"
                    className="admin-coupons__input"
                    placeholder="e.g. 100 (Leave empty for unlimited)"
                    value={formData.maxNumberOfUsage}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        maxNumberOfUsage: e.target.value,
                      })
                    }
                  />
                  <span className="admin-coupons__hint">
                    Maximum number of times this coupon can be used storewide.
                  </span>
                </div>
              </div>

              {/* If percentage was chosen, show per-user limit row */}
              {isPercentageCoupon(formData.couponType) && (
                <div className="admin-coupons__form-row">
                  <div className="admin-coupons__field flex-1">
                    <label htmlFor="coupon-user-limit">
                      Max Uses Per Customer
                    </label>
                    <input
                      id="coupon-user-limit"
                      type="number"
                      min="1"
                      className="admin-coupons__input"
                      placeholder="e.g. 1 (Default: 1 per customer)"
                      value={formData.maxUsagePerUser}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          maxUsagePerUser: e.target.value,
                        })
                      }
                    />
                    <span className="admin-coupons__hint">
                      Limits redemptions for each customer account.
                    </span>
                  </div>
                  <div className="flex-1" />
                </div>
              )}

              {/* Start & End Dates */}
              <div className="admin-coupons__form-row">
                <div className="admin-coupons__field flex-1">
                  <label htmlFor="coupon-start-date">Start Date & Time</label>
                  <input
                    id="coupon-start-date"
                    type="datetime-local"
                    className="admin-coupons__input"
                    value={formData.startDate}
                    onChange={(e) =>
                      setFormData({ ...formData, startDate: e.target.value })
                    }
                  />
                </div>

                <div className="admin-coupons__field flex-1">
                  <label htmlFor="coupon-end-date">
                    Expiration Date & Time (Optional)
                  </label>
                  <input
                    id="coupon-end-date"
                    type="datetime-local"
                    className={`admin-coupons__input ${
                      formErrors.endDate ? "has-error" : ""
                    }`}
                    value={formData.endDate}
                    onChange={(e) =>
                      setFormData({ ...formData, endDate: e.target.value })
                    }
                  />
                  {formErrors.endDate && (
                    <span className="admin-coupons__error-text">
                      {formErrors.endDate}
                    </span>
                  )}
                  <span className="admin-coupons__hint">
                    Leave blank if this coupon should not expire.
                  </span>
                </div>
              </div>

              {/* Enabled Switch */}
              <div className="admin-coupons__field-checkbox">
                <label className="admin-coupons__checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isEnabled}
                    onChange={(e) =>
                      setFormData({ ...formData, isEnabled: e.target.checked })
                    }
                  />
                  <span>
                    Enable coupon immediately upon saving (Active promotion)
                  </span>
                </label>
              </div>

              {/* Actions */}
              <div className="admin-modal__actions">
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => setModalMode(null)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary admin-coupons__save-btn"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 size={16} className="spin-animation" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>
                      {modalMode === "create" ? "Create Coupon" : "Save Changes"}
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. DELETE CONFIRMATION MODAL */}
      {deleteTarget && (
        <div
          className="admin-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            className="admin-modal admin-coupons__delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-coupons__delete-head">
              <div className="admin-coupons__delete-icon-wrap">
                <AlertTriangle size={24} />
              </div>
              <div>
                <h3>Delete Coupon?</h3>
                <p className="admin-coupons__delete-sub">
                  Are you sure you want to delete coupon{" "}
                  <strong>"{deleteTarget.code}"</strong>?
                </p>
              </div>
            </div>

            <div className="admin-coupons__delete-warning-box">
              <p>
                This will soft-delete and immediately deactivate the coupon.
                Past orders that already used this coupon will maintain their
                redemption history and discount records.
              </p>
            </div>

            <div className="admin-modal__actions">
              <button
                type="button"
                className="btn-outline"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger admin-coupons__confirm-delete-btn"
                onClick={handleDeleteCoupon}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 size={16} className="spin-animation" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={15} />
                    <span>Confirm Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
